import { type AggregatedAdjustment, type IAdjustment } from "~/models/adjustment"
import { type IWarehouse } from "~/models/warehouses"
import { formatDate } from "~/utils/datetime"
import { sumBy } from "lodash"
import { groupBy } from "~/utils/tsUtils"

/** Trimmed, lower-cased name of the adjustment `update_locked_variant_forecasts.py` creates for new channels. */
export const NEW_CHANNEL_ADJUSTMENT_NAME = "new channel - zero forecast"

export function buildSystemAdjustmentExplanation(args: {
  name: string | null | undefined
  startDate: Date
  endDate: Date
  channels: string[]
  variantCount?: number
}): string | null {
  const key = args.name?.trim().toLowerCase() ?? ""
  if (key !== NEW_CHANNEL_ADJUSTMENT_NAME) return null

  const period = `${formatDate(args.startDate)}–${formatDate(args.endDate)}`
  const channel = args.channels.filter(Boolean).join(", ") || "this channel"
  const action =
    "Remove the adjustment for a new forecast, or republish the demand plan if one's attached."

  return args.variantCount != null
    ? `${args.variantCount} variant(s) have a locked forecast in ${period}. To protect that decision, ${channel}'s forecast is set to zero for this period. ${action}`
    : `${channel}'s forecast is locked in ${period}, so it's set to zero for this period. ${action}`
}

/**
 * User-made adjustments deduped by name+period across variants. System
 * adjustments are noise (not campaigns the user logged) and are dropped.
 */
export function userAdjustments<
  T extends { name: string; startDate: Date; endDate: Date; isSystem: boolean },
>(rows: { adjustments: T[] }[]): T[] {
  const seen = new Map<string, T>()
  for (const row of rows) {
    for (const a of row.adjustments) {
      if (a.isSystem) continue
      seen.set(`${a.name}|${a.startDate.getTime()}|${a.endDate.getTime()}`, a)
    }
  }
  return [...seen.values()]
}

/** Adjustments whose period touches `[start, end]`, both ends inclusive. */
export function adjustmentsOverlapping<T extends { startDate: Date; endDate: Date }>(
  adjustments: T[],
  start: Date,
  end: Date,
): T[] {
  return adjustments.filter((a) => a.startDate <= end && a.endDate >= start)
}

// The server's aggregator key minus warehouseId: groups here are folded into Global.
const adjustmentAggregatorKey = (adjustment: IAdjustment) => adjustment.groupId

export const aggregateGlobal = (
  adjustments: IAdjustment[],
  warehouses: IWarehouse[],
): AggregatedAdjustment[] => {
  const aggregatedAdjustments: AggregatedAdjustment[] = []
  const groupedAdjustments = groupBy(adjustments, adjustmentAggregatorKey)

  Object.values(groupedAdjustments).forEach((group: IAdjustment[]) => {
    //should be aggregated to global
    if (group.length == warehouses!.length) {
      const variantIdSet = new Set<string>()
      const channelSet = new Set<string>()
      group.forEach((adj) => {
        adj.variantIds.forEach((id) => variantIdSet.add(id))
        adj.channels.forEach((ch) => channelSet.add(ch))
      })

      const aggregated: AggregatedAdjustment = {
        variantIds: Array.from(variantIdSet),
        value:
          sumBy(group, (adj) => adj.value) / (group[0]!.type === "percentage" ? group.length : 1),
        type: group[0]!.type,
        theme: group[0]!.theme,
        name: group[0]!.name,
        groupId: group[0]!.groupId,
        warehouseId: "Global",
        channels: Array.from(channelSet),
        startDate: group[0]!.startDate,
        endDate: group[0]!.endDate,
        updatedBy: group[0]!.updatedBy,
        updatedAt: group[0]!.updated,
        locksForecast: group[0]!.locksForecast,
        identifiers: group.map((adj) => ({
          variantIds: adj.variantIds,
          warehouseId: adj.warehouseId,
        })),
        referencedAdjustments: group,
      }

      aggregatedAdjustments.push(aggregated)
    } else {
      // should not be aggregated to global
      group.forEach((adj) => {
        const aggregated: AggregatedAdjustment = {
          variantIds: adj.variantIds,
          value: adj.value,
          type: adj.type,
          theme: adj.theme,
          name: adj.name,
          groupId: adj.groupId,
          warehouseId: adj.warehouseId,
          channels: adj.channels,
          startDate: adj.startDate,
          endDate: adj.endDate,
          updatedBy: adj.updatedBy,
          updatedAt: adj.updated,
          locksForecast: adj.locksForecast,
          identifiers: [{ variantIds: adj.variantIds, warehouseId: adj.warehouseId }],
          referencedAdjustments: [adj],
        }

        aggregatedAdjustments.push(aggregated)
      })
    }
  })

  return aggregatedAdjustments
}

/**
 * The per-week value a planner actually entered, recovered from an adjustment's stored rows.
 *
 * `add` and `set` adjustments are weight-split across their variant/warehouse/channel rows when
 * created (see `createAdjustment`), so no single row carries the typed number — summing one row
 * per distinct location restores it. Percentages are not split: every row carries the same
 * percentage, so the first row's value is already the entered one.
 *
 * `rows` must belong to one adjustment and one variant; the caller groups them. A location with
 * no forecast in the window contributes no row and no units, so leaving it out is correct.
 */
export function enteredAdjustmentValue(
  type: string,
  rows: { warehouseId: string; channel: string; value: number }[],
): number {
  if (rows.length === 0) return 0
  if (type === "percentage") return rows[0]!.value

  // One row per location: the same location repeats across the dates it covers, carrying the
  // same split value each time, so counting it once per location is what restores the total.
  const valueByLocation = new Map<string, number>()
  for (const row of rows) {
    valueByLocation.set(`${row.warehouseId}|${row.channel}`, row.value)
  }

  let total = 0
  for (const value of valueByLocation.values()) total += value
  return total
}
